import React, { Fragment, useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Button, Row, Col, Form, InputGroup } from 'react-bootstrap';
import classNames from 'classnames';

import {
  requestInsert,
  requestUpdate,
  requestInform as request,
  requestDuplicate
} from 'api/admin';
import {
  requestUpdate as requestUpdateUser,
  requestInform as requestUser
} from 'api/user';
import { factory } from 'api/haccp/commonApi';
import Flex from 'components/common/Flex';
import Content from 'components/common-sfims/Content';
import ListInContent from 'components/common-sfims/ListInContent';
import { onInputChange } from 'function/event';
import { getLoginInform } from 'function/login';
import { getErrorMessage } from 'function/message';
import { useConfirmModalState } from 'global/confirmModalState';
import useLoginState from 'global/loginState';
import useObjReducer from 'hooks/useObjReducer';
import { RESPONSE_CODE, ROUTER_PATH } from 'types/const';
import {
  HACCP_AUTH_TYPE,
  HACCP_AUTH_TYPE_SELECT,
  RETURN_YN,
  USE_YN_SELECT
} from 'types/type';

/**
 * 기준 정보 > 사원 관리 - 등록/수정
 */
const Detail = () => {
  /* variable */
  const { seq: loginSeq } = getLoginInform();

  /* router */
  const history = useHistory();
  const { pathname, state } = useLocation();
  const { seq } = useParams();

  /* recoil */
  const { setConfirmModal } = useConfirmModalState();
  const { setLogin } = useLoginState();

  /* state */
  // 사원 정보
  const [inform, setInform] = useObjReducer({});
  // 관리자 여부
  const [isAdmin, setIsAdmin] = useState(false);
  // ID 중복 확인 비활성화 여부
  const [isDisableDuplicate, setIsDisableDuplicate] = useState(false);
  // 설정 비활성화 여부
  const [isDisableSetting, setIsDisableSetting] = useState(false);
  // ID 중복 확인 여부
  const [isIdConfirm, setIsIdConfirm] = useState(false);
  // 로딩 여부
  const [isLoading, setIsLoading] = useState(true);
  // 비밀번호 확인
  const [passwordConfirm, setPasswordConfirm] = useState('');
  // 공장 목록
  const [plants, setPlants] = useState([]);
  // 사원 등록/수정 요청 데이터
  const [userData, setUserData] = useObjReducer({
    useYn: RETURN_YN.Y,
    authList: [],
    haccpYn: RETURN_YN.Y
  });

  /* watch */
  useEffect(() => {
    initRequest();
  }, []);
  useEffect(() => {
    // 관리자 여부 체크 시 기존 결재권한 선택 해제
    if (isAdmin) {
      setUserData({
        factoryCodeSeq: 1,
        haccpAuth: HACCP_AUTH_TYPE.ADMIN
      });
    }
  }, [isAdmin]);
  useEffect(() => {
    setUserData({
      authList: plants.map(({ pltCd, pltCode } = {}) => ({
        pltCd,
        pltCode,
        readYn: RETURN_YN.N,
        modYn: RETURN_YN.N
      }))
    });
  }, [plants]);
  useEffect(() => {
    // ID 변경 시 ID 중복 확인 여부 설정 해제
    setIsIdConfirm(false);
  }, [userData.userId]);
  useEffect(() => {
    const { pltCd } =
      plants.find(({ seq } = {}) => seq === userData.factoryCodeSeq) ?? {};

    setUserData({
      authList: userData.authList.map((value = {}) => ({
        ...value,
        readYn:
          RETURN_YN[
            userData.haccpAuth === HACCP_AUTH_TYPE.ADMIN ||
            pltCd === value.pltCd
              ? 'Y'
              : 'N'
          ],
        modYn:
          RETURN_YN[
            userData.haccpAuth === HACCP_AUTH_TYPE.ADMIN ||
            pltCd === value.pltCd
              ? 'Y'
              : 'N'
          ]
      }))
    });
  }, [userData.factoryCodeSeq, userData.haccpAuth]);

  /* method */
  // 초기 요청
  const initRequest = async () => {
    // 공장 목록 조회 요청
    await requestPlant();

    // 수정 화면인 경우 사원 정보 조회 요청
    if (seq || pathname === ROUTER_PATH.myPage) {
      await requestInform();
    }
  };
  // 사원 중복 여부 조회 요청
  const requestDuplicateId = async () => {
    let message = '입력이 가능한 ID 입니다.';

    setIsDisableDuplicate(true);

    try {
      if (!userData.userId) {
        throw { error: 'ID를 입력해주세요.' };
      }

      const { code, message } = await requestDuplicate({
        userId: userData.userId
      });

      // 아이디 중복 시
      if (code === RESPONSE_CODE.DuplicateInsert) {
        throw { error: '이미 등록된 ID 입니다. 다른 ID로 등록해주세요.' };
      }

      if (code !== RESPONSE_CODE.Success) {
        throw {
          code,
          error: message
        };
      }

      setIsIdConfirm(true);
    } catch (error) {
      message = getErrorMessage(error?.code, error?.error);
    } finally {
      setConfirmModal({
        isOpen: true,
        message
      });
      setIsDisableDuplicate(false);
    }
  };
  // 사원 정보 조회 요청
  const requestInform = async () => {
    try {
      const {
        data = {},
        code,
        message
      } = await (pathname === ROUTER_PATH.myPage ? requestUser : request)(
        pathname === ROUTER_PATH.myPage ? loginSeq : seq
      );

      if (code !== RESPONSE_CODE.Success) {
        throw {
          code,
          error: message
        };
      }

      const userData = {
        email: data.email,
        factoryCodeSeq: data.factoryCodeSeq
      };

      // 사원 관리인 경우
      if (pathname !== ROUTER_PATH.myPage) {
        Object.assign(userData, {
          useYn: data.useYn ?? RETURN_YN.Y,
          haccpAuth: data.haccpAuth,
          haccpYn: data.haccnYn ?? RETURN_YN.Y
        });
      }

      setInform(data);
      setIsAdmin(data.haccpAuth === HACCP_AUTH_TYPE.ADMIN);
      setUserData(userData);
    } catch (error) {
      setConfirmModal({
        isOpen: true,
        message: getErrorMessage(error?.code, error?.error)
      });

      // 사원 관리인 경우 목록 화면 표시
      if (pathname !== ROUTER_PATH.myPage) {
        showList();
      }
    }
  };
  // 공장 목록 조회 요청
  const requestPlant = async () => {
    setIsLoading(true);

    const { list = [] } = await factory();

    setIsLoading(false);
    setPlants(list);
  };
  // 목록 화면 표시
  const showList = () => {
    history.push(ROUTER_PATH.sfsh.standard.employee.list, state);
  };

  /* event */
  // 취소 클릭 시
  const onCancelClick = () => {
    // 마이 페이지인 경우 이전 화면 표시
    if (pathname === ROUTER_PATH.myPage) {
      history.goBack();

      return;
    }

    // 사원 관리인 경우 목록 화면 표시
    showList();
  };
  // 설정
  const onSubmit = async event => {
    let message =
      pathname === ROUTER_PATH.MyPage
        ? '변경하신 정보로 수정되었습니다.'
        : seq
        ? '사원정보가 수정되었습니다.'
        : '사원정보가 정상 등록되었습니다.';

    event.preventDefault();

    setIsDisableSetting(true);

    try {
      if (seq || pathname === ROUTER_PATH.myPage) {
        // 비밀번호 입력 시 유효성 확인
        if (userData.userPwd && passwordConfirm !== userData.userPwd) {
          throw {
            error:
              '변경한 비밀번호와 비밀번호 확인 재입력 정보가 일치하지 않습니다.\n다시 입력해주세요.'
          };
        }

        const { code, message } = await (pathname === ROUTER_PATH.myPage
          ? requestUpdateUser
          : requestUpdate)(
          pathname === ROUTER_PATH.myPage ? loginSeq : seq,
          userData
        );

        if (code !== RESPONSE_CODE.Success) {
          throw {
            code,
            error: message
          };
        }

        // 로그인 사용자 수정 시 로그인 정보 재설정
        if (loginSeq === Math.floor(seq)) {
          setLogin({
            adminYn: RETURN_YN[isAdmin ? 'Y' : 'N'],
            factoryCodeSeq: userData.factoryCodeSeq,
            authList: userData.authList
          });
        }
      } else {
        // ID 중복 확인을 하지 않은 경우
        if (!isIdConfirm) {
          throw { error: 'ID 중복 확인을 해주세요.' };
        }

        // 비밀번호 유효성 확인
        if (passwordConfirm !== userData.userPwd) {
          throw {
            error:
              '변경한 비밀번호와 비밀번호 확인 재입력 정보가 일치하지 않습니다.\n다시 입력해주세요.'
          };
        }

        const { code, message } = await requestInsert(userData);

        if (code !== RESPONSE_CODE.Success) {
          throw {
            code,
            error: message
          };
        }
      }

      // 사원 관리인 경우 목록 화면 표시
      if (pathname !== ROUTER_PATH.myPage) {
        showList();
      }
    } catch (error) {
      message = getErrorMessage(error?.code, error?.error);

      setIsDisableSetting(false);
    } finally {
      setConfirmModal({
        isOpen: true,
        message
      });
    }
  };

  /* view */
  return (
    <Form onSubmit={onSubmit}>
      <Content
        className={{
          root: 'mb-3',
          body: 'bg-light'
        }}
      >
        <Fragment key="title">계정정보</Fragment>
        <Fragment key="body">
          <Row className="g-3 mb-3">
            <Col sm={4}>
              <Form.Group>
                <Form.Label>
                  {!seq && pathname !== ROUTER_PATH.myPage && '*'}ID
                </Form.Label>
                <InputGroup>
                  <Form.Control
                    readOnly={seq || pathname === ROUTER_PATH.myPage}
                    size="sm"
                    type="text"
                    value={
                      (seq || pathname === ROUTER_PATH.myPage
                        ? inform
                        : userData
                      ).userId
                    }
                    autoFocus={!seq && pathname !== ROUTER_PATH.myPage}
                    maxLength={20}
                    name="userId"
                    placeholder="입력해주세요."
                    required
                    onChange={event => onInputChange(event, setUserData)}
                  />
                  {!seq && pathname !== ROUTER_PATH.myPage && (
                    <Button
                      disabled={isDisableDuplicate || isIdConfirm}
                      size="sm"
                      variant="success"
                      onClick={requestDuplicateId}
                    >
                      중복 확인
                    </Button>
                  )}
                </InputGroup>
              </Form.Group>
            </Col>
            <Col sm={4}>
              <Form.Group>
                <Form.Label>
                  {!seq && pathname !== ROUTER_PATH.myPage && '*'}비밀번호
                </Form.Label>
                <Form.Control
                  size="sm"
                  type="password"
                  value={userData.userPwd}
                  autoFocus={seq || pathname === ROUTER_PATH.myPage}
                  maxLength={20}
                  name="userPwd"
                  placeholder={`${
                    seq || pathname === ROUTER_PATH.myPage ? '수정 시 ' : ''
                  }입력해주세요.`}
                  required={!seq && pathname !== ROUTER_PATH.myPage}
                  onChange={event => onInputChange(event, setUserData)}
                />
              </Form.Group>
            </Col>
            <Col sm={4}>
              <Form.Group>
                <Form.Label>
                  {!seq && pathname !== ROUTER_PATH.myPage && '*'}비밀번호 확인
                </Form.Label>
                <Form.Control
                  size="sm"
                  type="password"
                  value={passwordConfirm}
                  placeholder={`${
                    seq || pathname === ROUTER_PATH.myPage ? '수정 시 ' : ''
                  }입력해주세요.`}
                  required={!seq && pathname !== ROUTER_PATH.myPage}
                  onChange={({ target: { value } }) => {
                    setPasswordConfirm(value);
                  }}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="g-3 mb-3">
            <Col sm={4}>
              <Form.Group>
                <Form.Label>
                  {!seq && pathname !== ROUTER_PATH.myPage && '*'}담당자명
                </Form.Label>
                <Form.Control
                  readOnly={seq || pathname === ROUTER_PATH.myPage}
                  size="sm"
                  type="text"
                  value={
                    (seq || pathname === ROUTER_PATH.myPage ? inform : userData)
                      .userNm
                  }
                  maxLength={20}
                  name="userNm"
                  placeholder="입력해주세요."
                  required
                  onChange={event => onInputChange(event, setUserData)}
                />
              </Form.Group>
            </Col>
            <Col sm={4}>
              <Form.Group>
                <Form.Label>*E-Mail</Form.Label>
                <Form.Control
                  size="sm"
                  type="email"
                  value={userData.email}
                  maxLength={50}
                  name="email"
                  placeholder="입력해주세요."
                  required
                  onChange={event => onInputChange(event, setUserData)}
                />
              </Form.Group>
            </Col>
            {pathname !== ROUTER_PATH.myPage && (
              <Col sm={4}>
                <Form.Group>
                  <Form.Label>*사용유무</Form.Label>
                  <Flex>
                    {USE_YN_SELECT.map(({ key, value } = {}, index) => (
                      <Form.Check
                        key={`useYn.${key}`}
                        label={value}
                        type="radio"
                        checked={key === userData.useYn}
                        className={classNames({ 'ms-3': index > 0 })}
                        name="useYn"
                        value={key}
                        onChange={event => onInputChange(event, setUserData)}
                      />
                    ))}
                  </Flex>
                </Form.Group>
              </Col>
            )}
          </Row>
        </Fragment>
      </Content>
      <ListInContent
        className={{
          body: 'bg-light',
          table: 'mt-3'
        }}
        columns={[
          '공장 코드',
          '공장명',
          '관리자 여부',
          ...Object.entries(HACCP_AUTH_TYPE_SELECT)
            .filter(([key]) => key !== HACCP_AUTH_TYPE.ADMIN)
            .map(([, value]) => value)
        ]}
        hover={false}
        showLoading={isLoading}
      >
        <Fragment key="title">권한정보</Fragment>
        <Fragment key="datas">
          {plants.map(({ seq, pltCode, pltNmKo } = {}, index) => (
            <tr key={`plants${seq}`}>
              <th className="bg-200 text-700 border-300 border-bottom">
                {pltCode}
              </th>
              <th className="bg-200 text-700 border-300 border-bottom">
                {pltNmKo}
              </th>
              {index === 0 && (
                <td className="align-middle" rowSpan={plants.length}>
                  <Form.Check
                    disabled={pathname === ROUTER_PATH.myPage}
                    checked={isAdmin}
                    onChange={({ target: { checked } }) => setIsAdmin(checked)}
                  />
                </td>
              )}
              {Object.entries(HACCP_AUTH_TYPE_SELECT)
                .filter(([key]) => key !== HACCP_AUTH_TYPE.ADMIN)
                .map(([haccpAuth]) => (
                  <td key={`plants${seq}-${haccpAuth}`}>
                    <Form.Check
                      disabled={isAdmin || pathname === ROUTER_PATH.myPage}
                      type="radio"
                      checked={
                        seq === userData.factoryCodeSeq &&
                        haccpAuth ===
                          (pathname === ROUTER_PATH.myPage ? inform : userData)
                            .haccpAuth
                      }
                      name="haccpAuth"
                      required={!isAdmin && pathname !== ROUTER_PATH.myPage}
                      onChange={() => {
                        setUserData({
                          factoryCodeSeq: seq,
                          haccpAuth
                        });
                      }}
                    />
                  </td>
                ))}
            </tr>
          ))}
        </Fragment>
        <Fragment key="footerButton">
          <Button
            size="sm"
            variant="secondary"
            className="ms-auto"
            onClick={onCancelClick}
          >
            취소
          </Button>
          <Button
            disabled={isDisableSetting}
            size="sm"
            type="submit"
            className="ms-2"
          >
            {seq || pathname === ROUTER_PATH.myPage ? '수정' : '등록'}
          </Button>
        </Fragment>
      </ListInContent>
    </Form>
  );
};

export default Detail;
